import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Minimal resources used in the app (EN/RU)
const resources = {
  en: {
    translation: {
      // Auth
      "auth.title": "Login",
      "auth.username": "Username",
      "auth.password": "Password",
      "auth.login": "Login",
      "auth.logging_in": "Logging in...",
      "auth.error": "Invalid username or password",
      "auth.redirect_message": "Please log in to access {{path}}",

      // Language page
      "language.title": "Language",
      "language.search_placeholder": "Search language",
      "language.languages.en": "English",
      "language.languages.ru": "Russian",

      //game rules
      "game_rules.overview": "Overview",
      "game_rules.overview_text": ` This is a multiplier-based crash game in which the player places a bet
          before the start of a round. A character (The Jester) begins to
          ascend, and the multiplier increases continuously. The objective is to
          cash out the bet before the crash event occurs. The longer the
          character climbs, the higher the potential multiplier, but if the
          character falls before the player cashes out, the bet is lost.`,
      "game_rules.rtp": "Return to Player (RTP)",
      "game_rules.rtp_text": `The theoretical return to player (RTP) is 97%. This means that, over
          an extended period of play, players are expected to receive 97% of the
          total amount wagered.`,
      "game_rules.game_flow": "Game Flow",
      "game_rules.game_flow_text": `Each game round follows this sequence:`,
      "game_rules.betting_phase":
        "Betting Phase (≈ 5 seconds) – Players place their bets before the round begins.",
      "game_rules.active_round":
        "Active Round – The multiplier begins to increase.",
      "game_rules.cash_out_option":
        "Cash Out Option – Players can manually cash out at any time during the round.",
      "game_rules.crash_event":
        "Crash Event – The round ends when the crash occurs, stopping all remaining active bets.",
      "game_rules.after_crash":
        "After a round ends, betting phase begins before the next round starts.",
      "game_rules.multiple_bets.title": "Multiple Bets",
      "game_rules.multiple_bets": `Players can place up to two independent bets per round. Each bet may
          have a different value and a separate cashout point. One bet may be
          cashed out while the other remains active.`,
      "game_rules.how_wins_are_calculated": "How Wins Are Calculated",
      "game_rules.how_wins_are_calculated_text": `If the player successfully cashes out before the crash, the win is calculated by multiplying the bet amount by the multiplier displayed at the moment of cash out.
Win = Bet × Multiplier at Cashout
If the crash occurs before the player cashes out, the bet is forfeited.`,
      "game_rules.auto_play_and_auto_cashout": "Auto Play and Auto Cashout",
      "game_rules.auto_play": `Auto Play allows the player to automatically place bets for a set
          number of rounds, with optional stop conditions such as:`,
      "game_rules.stop_on_profit":
        "Stop if total win exceeds a selected amount.",
      "game_rules.stop_on_loss":
        "Stop if total loss exceeds a selected amount.",
      "game_rules.stop_on_single_win":
        "Stop if a single win exceeds a selected amount.",
      "game_rules.auto_cashout":
        "Auto Cashout enables the system to automatically cash out a bet when a specified multiplier is reached. This reduces the risk of missing manual cashout.",
      "game_rules.maximum_win.title": "Maximum Win",
      "game_rules.maximum_win": `The maximum possible multiplier in a single round is 10,000x the bet
          amount. Please note that the actual maximum win may vary depending on
          the time of the cashout.`,
      "game_rules.responsible_gaming_notice.title": "Responsible Gaming Notice",
      "game_rules.responsible_gaming_notice": `This game is designed for entertainment purposes. Players are
          encouraged to gamble responsibly, manage their bankroll wisely, and
          set personal limits where necessary.`,
      "game_rules.randomness_and_fairness.title": "Randomness and Fairness",
      "game_rules.randomness_and_fairness": `Each round outcome is determined by a provably fair algorithm,
          ensuring that all results are random and verifiable. Players can
          access the fairness section within the game to review seed values and
          validate round results.`,
      "game_rules.title": "Game Rules",
      "game_rules.disconnection_policy": "Disconnection Policy",
      "game_rules.disconnection_policy_text_1": `If a player disconnects during the betting phase, their bets are
          automatically cancelled.`,
      "game_rules.disconnection_policy_text_2": `If a player disconnects during the active round, their active bets
          are cancelled, and the player loses the bet amount.`,
      "game_rules.disconnection_policy_text_3": `If a player disconnects during the cashout phase, their cashout is
          automatically processed.`,
      //ProvablyFairDescription
      "provably_fair.title": "Provably Fair",
      "provably_fair.what_is_provably_fair": "What is provably fair?",
      "provably_fair.how_it_works": "How it works?",
      "provably_fair.how_to_check": "How to check?",
      "provably_fair.description": `This game uses a cryptographic system called Provably Fair, which
          ensures the outcome of each round is 100% transparent and verifiable.
          It is impossible for the operator or any third party to manipulate
          game results after the round begins.`,
      "provably_fair.crash_point": `The crash point (multiplier) for each round is not generated in
          advance or by the operator alone. Instead, it is the result of a
          combination of randomly generated values (seeds) provided by:`,
      "provably_fair.server": "The game server",
      "provably_fair.player_seeds":
        "The first three players who place bets in that round",
      "provably_fair.round_verification": `Each round uses a server seed generated by the operator and three
          client seeds from players. These values are combined and hashed using
          the SHA512 algorithm to determine the final round result. The server
          seed is hashed and shown publicly before the round starts, so it
          cannot be changed after the fact.`,
      "provably_fair.server_seed": "Server's seed",
      "provably_fair.player_seed": "Player {{number}} seed",
      "provably_fair.hints": "Hints",
      "provably_fair.hint_1":
        "You can change your client seed at any time in the settings.",
      "provably_fair.hint_2":
        "To participate in round result generation, place your bet early - only the first three players per round are included in the calculation.",
      "provably_fair.hint_3": "Open the My Bets section.",
      "provably_fair.hint_4": "Select any completed round.",
      "provably_fair.hint_5": "View the seeds, combined hash, and result.",
      "provably_fair.hint_6":
        "Use a SHA512 hash calculator to confirm the integrity of the outcome.",
      "provably_fair.combined_seed": "Combined seed",
      "provably_fair.combined_hash": "Combined SHA512 Hash",
      "provably_fair.round_result": "Round result",
      "provably_fair.description_text": `This game uses Provably Fair technology to ensure that each result is
          generated transparently. You can change your seed and independently
          verify the fairness of every round`,
      "provably_fair.next_seed": "Your next seed",
      "provably_fair.server_next_hash": "Server's next hash",
      // onboarding
      "onboarding.title": "How to Play",
      "onboarding.place_bet": "Place your bet",
      "onboarding.place_bet_text": `Before the round starts, place your bet. The higher the bet, the higher the potential multiplier.`,
      "onboarding.cash_out": "Cash out",
      "onboarding.cash_out_text": `Cash out before the crash to win. The longer you wait, the higher the potential multiplier.`,
      "onboarding.bonus_bets": "Bonus bets",
      "onboarding.bonus_bets_text": `Lose 10 bets to gain 1 bonus bet. No wager required.`,
      "onboarding.climb_begins": "before the climb begins",
      "onboarding.get_ready":
        "Get ready to scale the wall and chase the multiplier!",
      "onboarding.next": "Next",
      "onboarding.climb_higher": "Climb higher",
      "onboarding.win_bigger": "Win bigger!",
      "onboarding.cash_out_multiplier":
        "Your multiplier increases in real time up to 10'000x",
      "onboarding.get_started": "Get started",
      "onboarding.before_jester_slips": "before Jester's slips!",
      "onboarding.make_it_manually":
        "Make it manually or use auto cashout feature",
      "onboarding.auto_cashout": "Auto cashout",
      "onboarding.free_bonus_bet": "Collect 10 points for a free Bonus Bet",
      "onboarding.lose_bet_gain_point": "lost a bet? gain 1 point!",
      "onboarding.read_rules": "or <h>read Game Rules</h>",
      //MyBetInner
      "my_bets.round": "Round {{roundId}}",
      "my_bets.server_seed": "Server Seed:",
      "my_bets.generated_on_our_side": "Generated on our side",
      "my_bets.public_hash": "Public Hash:",
      "my_bets.server_seed_hashed": "Server seed hashed (commitment)",
      "my_bets.client_seeds": "Client Seeds:",
      "my_bets.generated_on_players_side": " Generated on players side",
      "my_bets.combined_sha_256_hash": "Combined SHA 256 Hash:",
      "my_bets.above_seeds_combined": `Above seeds combined and converted to Sha512 Hash. This is your
              game result`,
      "my_bets.hex": "Hex:",
      "my_bets.dec": "Dec:",
      "my_bets.odds": "Odds:",
      "my_bets.not_revealed_yet":
        "This round has not been revealed yet — the server seed, crash point and the values derived from them appear after the reveal.",
      //RoundsDetails
      "rounds_details.all_bets": "All Bets",
      "rounds_details.my_bets": "My Bets",
      "rounds_details.leaderboard": "Leaderboard",
      "rounds_details.date_time": "Date/Time",
      "rounds_details.x": "X",
      "rounds_details.round_x": "Round X",
      "rounds_details.bet_usd": "Bet USD",
      "rounds_details.win_usd": "Win USD",
      "rounds_details.load_more": "Load more",
      "rounds_details.round_result": "Round result",
      "rounds_details.bet": "Bet",
      "rounds_details.win": "Win",
      "rounds_details.rounds": "Rounds",
      "rounds_details.day": "Day",
      "rounds_details.week": "Week",
      "rounds_details.month": "Month",
      "rounds_details.player": "Player",
      //Chat
      "chat.title": "Chat",
      "chat.open": "Open chat",
      "chat.close": "Close chat",
      "chat.placeholder": "Type a message ...",
      "chat.send": "Send",
      "chat.unavailable": "Chat unavailable",
      "chat.empty": "No messages yet. Be the first.",
      "chat.history_failed": "Failed to load history",
      "chat.refresh": "Refresh",
      "chat.load_older": "Show earlier",
      "chat.loading": "Loading…",
      "chat.deleted": "Message deleted",
      "chat.jump_to_bottom": "Down",
      "chat.retry": "Try again",
      "chat.status_sending": "Sending…",
      "chat.status_sent": "Sent",
      "chat.status_unconfirmed": "Delivery not confirmed",
      "chat.status_failed": "Failed to send",
      "chat.hint_invalid_char": "Invalid character",
      "chat.hint_too_long": "No more than {{limit}} characters",
      "chat.hint_too_large": "Message is too large",
      "chat.error_empty_message": "Empty message",
      "chat.error_control_char": "Invalid characters",
      "chat.error_message_too_long": "Message is too long",
      "chat.error_rate_limit": "Too often, please wait",
      "chat.error_chat_disabled": "Chat is unavailable",
      //Game limits
      "game_limits.title": "Game limits",
      "game_limits.minimum_bet_usd": "Minimum bet USD",
      "game_limits.maximum_bet_usd": "Maximum bet USD",
      "game_limits.maximum_win_for_one_bet_usd": "Maximum win for one bet USD",
      //Burger menu
      "auth.sound": "Sound",
      "auth.music": "Music",
      "auth.language": "Language",
      "auth.how_to_play": "How to play",
      "auth.game_rules": "Game rules",
      "auth.game_limits": "Game limits",
      "auth.provably_fair_settings": "Provably fair settings",
      "auth.change_avatar": "Change Avatar",
      //Main page
      "main.place_bet": "Place bet",
      "main.cash_out": "Cash out",
      "main.cancel": "Cancel",
      "main.auto_cash_out": "Auto cash out",
      "main.auto_play": "Auto play",
      "main.stop_auto_play": "Stop auto play",
      "main.bet_amount": "Bet amount",
      "main.cash_out_multiplier": "Cash out multiplier",
      "main.cash_out_amount": "Cash out amount",
      "main.auto_play_options": "Auto play options",
      "main.number_of_rounds": "Number of rounds",
      "main.stop_if_cash_decreases_by": "Stop if cash decreases by",
      "main.stop_if_cash_increases_by": "Stop if cash increases by",
      "main.stop_if_single_win_exceeds": "Stop if single win exceeds",
      "main.reset": "Reset",
      "main.start": "Start",
      "main.place_your_bets": "Place your bets!",
      "main.awaiting_next_round": "Awaiting next round",
      "main.waiting_for_next_round": "Waiting for next round",
      "main.cannot_change_auto_cashout":
        "Cannot change auto cashout during climbing while you have an active bet",
      "main.please_specify_stop_condition":
        "Please, specify at least one stop condition",

      // Rooms
      "rooms.title": "Choose a room",
      "rooms.subtitle": "Rooms are provided by the lobby catalog",
      "rooms.enter": "Enter",
      "rooms.loading": "Loading rooms\u2026",
      "rooms.unavailable": "Rooms are temporarily unavailable",
      "rooms.retry": "Try again",
      "rooms.connection_failed":
        "Could not connect to this room. You can retry, or go back to the room list.",
      "rooms.retry_connection": "Retry",
      "rooms.back_to_catalog": "Back to rooms",
      "rooms.assets_loading": "Loading game \u2014 {{percent}}%",
      "rooms.assets_ready": "Game is ready",
    },
  },
  ru: {
    translation: {
      // Auth
      "auth.title": "Вход",
      "auth.username": "Логин",
      "auth.password": "Пароль",
      "auth.login": "Войти",
      "auth.logging_in": "Входим...",
      "auth.error": "Неверный логин или пароль",
      "auth.redirect_message": "Войдите, чтобы перейти к {{path}}",

      // Language page
      "language.title": "Язык",
      "language.search_placeholder": "Поиск языка",
      "language.languages.en": "Английский",
      "language.languages.ru": "Русский",

      //game rules
      "game_rules.overview": "Обзор",
      "game_rules.overview_text": ` Это мультипликаторная игра, в которой игрок ставит перед началом
          раунда. Персонаж (Джокер) начинает подниматься, и мультипликатор
          увеличивается постепенно. Цель состоит в том, чтобы вывести ставку до
          момента краша. Чем дольше персонаж поднимается, тем выше
          потенциальный мультипликатор, но если персонаж падает до тех пор, пока
          игрок не вывел ставку, ставка теряется.`,
      "game_rules.rtp": "Возврат игроку (RTP)",
      "game_rules.rtp_text": `Теоретический возврат игроку (RTP) составляет 97%. Это означает, что
          игроки должны получить 97% от общей суммы ставок в течение
          расширенного периода игры.`,
      "game_rules.game_flow": "Ход игры",
      "game_rules.game_flow_text": `Каждый игровой раунд проходит по следующей схеме:`,
      "game_rules.betting_phase":
        "Фаза ставок (≈ 5 секунд) – Игроки делают свои ставки перед началом раунда.",
      "game_rules.active_round":
        "Активный раунд – Мультипликатор начинает увеличиваться.",
      "game_rules.cash_out_option":
        "Вывод средств – Игроки могут вручную вывести средства в любой момент.",
      "game_rules.crash_event":
        "Событие краша – Раунд заканчивается, когда происходит краш, останавливая все оставшиеся активные ставки.",
      "game_rules.after_crash":
        "После завершения раунда начинается фаза ставок перед началом следующего раунда.",
      "game_rules.multiple_bets.title": "Множественные ставки",
      "game_rules.multiple_bets": `Игроки могут сделать до двух независимых ставок на каждом раунде. Каждая ставка может иметь разную сумму и отдельную точку вывода. Одна ставка может быть выведена, в то время как другая остается активной.`,
      "game_rules.auto_play_and_auto_cashout":
        "Автоматическая игра и автовывод",
      "game_rules.auto_play": `Автоматическая игра позволяет игроку автоматически делать ставки на заданное количество раундов с необязательными условиями остановки, такими как:`,
      "game_rules.stop_on_profit":
        "Остановить, если общий выигрыш превышает выбранную сумму.",
      "game_rules.stop_on_loss":
        "Остановить, если общий проигрыш превышает выбранную сумму.",
      "game_rules.stop_on_single_win":
        "Остановить, если единичный выигрыш превышает выбранную сумму.",
      "game_rules.auto_cashout":
        "Автовывод позволяет системе автоматически выводить ставки при достижении указанного мультипликатора. Это снижает риск пропуска ручного вывода.",
      "game_rules.maximum_win.title": "Максимальный выигрыш",
      "game_rules.maximum_win": `Максимальный возможный мультипликатор в одном раунде составляет 10 000x. Пожалуйста, обратите внимание, что фактический максимальный выигрыш может варьироваться в зависимости от времени вывода.`,
      "game_rules.responsible_gaming_notice.title":
        "Уведомление о ответственной игре",
      "game_rules.responsible_gaming_notice": `Эта игра предназначена для развлекательных целей. Игроки должны играть ответственно, умело управлять банкроллом и устанавливать личные лимиты при необходимости.`,
      "game_rules.randomness_and_fairness.title":
        "Случайность и справедливость",
      "game_rules.randomness_and_fairness": `Каждый результат раунда определяется с помощью алгоритма, который основан на криптографии. Это гарантирует, что все результаты являются случайными и проверяемыми. Игроки могут проверить значения seed и результаты раунда в разделе "Справедливость" в игре.`,
      "game_rules.title": "Правила игры",
      "game_rules.how_wins_are_calculated": "Как вычисляются выигрыши",
      "game_rules.how_wins_are_calculated_text": `Если игрок успешно выводит средства до краша, выигрыш вычисляется путем умножения суммы ставки на показанный мультипликатор в момент вывода.
          Если краш произошел до тех пор, пока игрок не вывел средства, ставка теряется.`,
      "game_rules.disconnection_policy": "Политика отключения",
      "game_rules.disconnection_policy_text_1": `Если игрок отключается во время активной ставки, система попытается автоматически вывести ставку по текущему мультипликатору, если краш еще не произошел.`,
      "game_rules.disconnection_policy_text_2": `Если игрок отключается до начала раунда и ставка не была принята, ставка возвращается.`,
      "game_rules.disconnection_policy_text_3": `В случае технических проблем, связанных с производительностью игры или неполадками, все затронутые ставки будут возвращены, а любые связанные выигрыши будут аннулированы.`,
      //ProvablyFairDescription
      "provably_fair.title": "Provably Fair",
      "provably_fair.what_is_provably_fair": "Что такое Provably Fair?",
      "provably_fair.how_it_works": "Как это работает?",
      "provably_fair.how_to_check": "Как проверить?",
      "provably_fair.description": `Эта игра использует криптографическую систему, называемую Provably Fair, которая
          гарантирует, что результат каждого раунда является 100% прозрачным и проверяемым.
          Невозможно для оператора или любого третьего лица манипулировать результатами игры после начала раунда.`,
      "provably_fair.crash_point": `Коэффициент краша (мультипликатор) для каждого раунда не генерируется заранее или только оператором. Вместо этого это результат комбинации случайно сгенерированных значений (сидов), предоставленных:`,
      "provably_fair.server": "Игровой сервер",
      "provably_fair.player_seeds":
        "Первые три игрока, которые делают ставки в этом раунде",
      "provably_fair.round_verification": `Каждый раунд использует серверный сид, сгенерированный оператором, и три сида от игроков. Эти значения объединяются и хэшируются с помощью алгоритма SHA512, чтобы определить окончательный результат раунда. Серверный сид хэшируется и публично показывается перед началом раунда, поэтому его нельзя изменить после начала раунда.`,
      "provably_fair.server_seed": "Сид сервера",
      "provably_fair.player_seed": "Сид игрока {{number}}",
      "provably_fair.hints": "Подсказки",
      "provably_fair.hint_1":
        "Вы можете изменить свой сид клиента в любое время в настройках.",
      "provably_fair.hint_2":
        "Чтобы принять участие в генерации результатов раунда, сделайте ставку рано - только первые три игрока на каждом раунде включаются в расчет.",
      "provably_fair.hint_3": "Откройте раздел Мои ставки.",
      "provably_fair.hint_4": "Выберите любой завершенный раунд.",
      "provably_fair.hint_5":
        "Просмотрите сиды, комбинированный хэш и результат.",
      "provably_fair.hint_6":
        "Используйте калькулятор SHA512 хэша, чтобы подтвердить целостность результата.",
      "provably_fair.combined_seed": "Комбинированный сид",
      "provably_fair.combined_hash": "Комбинированный SHA512 Hash",
      "provably_fair.round_result": "Результат раунда",
      "provably_fair.description_text": `Эта игра использует технологию Provably Fair, чтобы гарантировать, что каждый результат генерируется прозрачно. Вы можете изменить свой сид и независимо проверить справедливость каждого раунда`,
      "provably_fair.next_seed": "Ваш следующий сид",
      "provably_fair.server_next_hash": "Серверный следующий хэш",
      // onboarding
      "onboarding.title": "Как играть",
      "onboarding.place_bet": "Сделать ставку",
      "onboarding.place_bet_text": `Перед началом раунда сделайте ставку. Чем больше ставка, тем выше потенциальный мультипликатор.`,
      "onboarding.cash_out": "Вывести средства",
      "onboarding.cash_out_text": `Выведите средства до краша, чтобы выиграть. Чем дольше вы ждете, тем выше потенциальный мультипликатор.`,
      "onboarding.bonus_bets": "Бонусные ставки",
      "onboarding.bonus_bets_text": `Потеряйте 10 ставок, чтобы получить 1 бесплатную бонусную ставку. Без необходимости делать ставки.`,
      "onboarding.climb_begins": "Перед началом взрыва",
      "onboarding.get_ready":
        "Готовьтесь, чтобы подниматься по стене и идти за мультипликатором!",
      "onboarding.next": "Далее",
      "onboarding.climb_higher": "Подниматься выше",
      "onboarding.win_bigger": "Выигрывать больше!",
      "onboarding.cash_out_multiplier":
        "Ваш мультипликатор увеличивается в режиме реального времени до 10'000x",
      "onboarding.get_started": "Начать",
      "onboarding.before_jester_slips": "Перед тем, как падет Джокер!",
      "onboarding.make_it_manually":
        "Сделайте это вручную или используйте функцию автовывода",
      "onboarding.auto_cashout": "Автовывод",
      "onboarding.free_bonus_bet":
        "Соберите 10 очков, чтобы получить бесплатную бонусную ставку",
      "onboarding.lose_bet_gain_point": "Потеряли ставку? получите 1 очко!",
      "onboarding.read_rules": "или <h>прочитать Правила игры</h>",
      //MyBetInner
      "my_bets.round": "Раунд {{roundId}}",
      "my_bets.server_seed": "Серверный сид:",
      "my_bets.generated_on_our_side": "Сгенерирован на нашей стороне",
      "my_bets.public_hash": "Публичный хэш:",
      "my_bets.server_seed_hashed": "Серверный сид хэширован (коммитмент)",
      "my_bets.client_seeds": "Сиды клиентов:",
      "my_bets.generated_on_players_side": "Сгенерирован на стороне игроков",
      "my_bets.combined_sha_256_hash": "Комбинированный SHA 256 Хэш:",
      "my_bets.above_seeds_combined": `Выше сиды объединены и конвертированы в Sha512 Хэш. Это ваш результат игры`,
      "my_bets.hex": "Hex:",
      "my_bets.dec": "Dec:",
      "my_bets.odds": "Odds:",
      "my_bets.not_revealed_yet":
        "Раунд ещё не раскрыт — server seed, crash point и производные от них значения появятся после раскрытия.",
      //RoundsDetails
      "rounds_details.all_bets": "Все ставки",
      "rounds_details.my_bets": "Мои ставки",
      "rounds_details.leaderboard": "Рекорды",
      "rounds_details.date_time": "Дата/Время",
      "rounds_details.x": "X",
      "rounds_details.round_x": "Раунд X",
      "rounds_details.bet_usd": "Cтавка USD",
      "rounds_details.win_usd": "Выигрыш  USD",
      "rounds_details.load_more": "Загрузить еще",
      "rounds_details.round_result": "Результат раунда",
      "rounds_details.bet": "Ставка",
      "rounds_details.win": "Выигрыш",
      "rounds_details.rounds": "Раунды",
      "rounds_details.day": "День",
      "rounds_details.week": "Неделя",
      "rounds_details.month": "Месяц",
      "rounds_details.player": "Игрок",
      //Chat
      "chat.title": "Чат",
      "chat.open": "Открыть чат",
      "chat.close": "Закрыть чат",
      "chat.placeholder": "Введите сообщение ...",
      "chat.send": "Отправить",
      "chat.unavailable": "Чат недоступен",
      "chat.empty": "Пока пусто. Напишите первым.",
      "chat.history_failed": "Не удалось загрузить историю",
      "chat.refresh": "Обновить",
      "chat.load_older": "Показать раньше",
      "chat.loading": "Загружаем…",
      "chat.deleted": "Сообщение удалено",
      "chat.jump_to_bottom": "Вниз",
      "chat.retry": "Ещё раз",
      "chat.status_sending": "Отправляется…",
      "chat.status_sent": "Отправлено",
      "chat.status_unconfirmed": "Доставка не подтверждена",
      "chat.status_failed": "Не удалось отправить",
      "chat.hint_invalid_char": "Некорректный символ",
      "chat.hint_too_long": "Не больше {{limit}} символов",
      "chat.hint_too_large": "Сообщение слишком объёмное",
      "chat.error_empty_message": "Пустое сообщение",
      "chat.error_control_char": "Недопустимые символы",
      "chat.error_message_too_long": "Сообщение слишком длинное",
      "chat.error_rate_limit": "Слишком часто, подождите",
      "chat.error_chat_disabled": "Чат недоступен",
      //Game limits
      "game_limits.title": "Лимиты игры",
      "game_limits.minimum_bet_usd": "Минимальная ставка USD",
      "game_limits.maximum_bet_usd": "Максимальная ставка USD",
      "game_limits.maximum_win_for_one_bet_usd":
        "Максимальный выигрыш за одну ставку USD",
      //Burger menu
      "auth.sound": "Звук",
      "auth.music": "Музыка",
      "auth.language": "Язык",
      "auth.how_to_play": "Как играть",
      "auth.game_rules": "Правила игры",
      "auth.game_limits": "Лимиты игры",
      "auth.provably_fair_settings": "Настройки Provably Fair",
      "auth.change_avatar": "Изменить аватар",
      //Main page
      "main.place_bet": "Сделать ставку",
      "main.cash_out": "Вывести средства",
      "main.cancel": "Отмена",
      "main.auto_cash_out": "Автовывод",
      "main.auto_play": "Автоплей",
      "main.stop_auto_play": "Остановить автоплей",
      "main.bet_amount": "Сумма ставки",
      "main.cash_out_multiplier": "Мультипликатор вывода",
      "main.cash_out_amount": "Сумма вывода",
      "main.auto_play_options": "Настройки автоплея",
      "main.number_of_rounds": "Количество раундов",
      "main.stop_if_cash_decreases_by": "Остановить, если кэш уменьшается на",
      "main.stop_if_cash_increases_by": "Остановить, если кэш увеличивается на",
      "main.stop_if_single_win_exceeds":
        "Остановить, если единичный выигрыш превышает",
      "main.reset": "Сбросить",
      "main.start": "Начать",
      "main.place_your_bets": "Сделайте свои ставки!",
      "main.awaiting_next_round": "Ожидание следующего раунда",
      "main.waiting_for_next_round": "Ожидание следующего раунда",
      "main.cannot_change_auto_cashout":
        "Нельзя менять автовывод во время краша, пока у вас есть активная ставка",
      "main.please_specify_stop_condition":
        "Пожалуйста, укажите хотя бы одно условие остановки",

      // Rooms
      "rooms.title": "Выберите комнату",
      "rooms.subtitle": "Список комнат приходит из каталога лобби",
      "rooms.enter": "Войти",
      "rooms.loading": "Загружаем комнаты\u2026",
      "rooms.unavailable": "Комнаты временно недоступны",
      "rooms.retry": "Повторить",
      "rooms.connection_failed":
        "Не удалось подключиться к комнате. Можно повторить попытку или вернуться к списку комнат.",
      "rooms.retry_connection": "Повторить подключение",
      "rooms.back_to_catalog": "К списку комнат",
      "rooms.assets_loading": "Загружаем игру \u2014 {{percent}}%",
      "rooms.assets_ready": "Игра готова",
    },
  },
} as const;

function detectInitialLng(): string {
  try {
    const saved =
      localStorage.getItem("lang") || localStorage.getItem("i18nextLng");
    if (saved && (saved === "en" || saved === "ru")) return saved;
    const nav = typeof navigator !== "undefined" ? navigator.language : "en";
    return /^ru/i.test(nav) ? "ru" : "en";
  } catch {
    return "en";
  }
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: detectInitialLng(),
    fallbackLng: "en",
    interpolation: { escapeValue: false },
  });

  i18n.on("languageChanged", (lng) => {
    localStorage.setItem("lang", lng);
  });
}

export default i18n;
